import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";

export interface UserProgress {
  id: string;
  name: string;
  profileUrl: string;
  completed: number;
  skipped: number;
  total: number;
}

interface SingleTaskProgressProps {
  user: UserProgress;
  currentProgress: number;
}

export const SingleTaskProgress: React.FC<SingleTaskProgressProps> = ({
  user,
  currentProgress,
}) => {
  const { t } = useTranslation();

  const progressBarRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [animationReady, setAnimationReady] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);

  useEffect(() => {
    if (progressBarRef.current && imageRef.current) {
      requestAnimationFrame(() => {
        setAnimationReady(true);
      });
    }
  }, []);

  return (
    <div ref={progressBarRef} className="relative mr-3 mb-6 h-4">
      <div className="absolute inset-0 overflow-hidden rounded-xl bg-background-extra-light">
        <div
          className="h-full w-[120%] -translate-x-[10%]"
          style={{
            background: `repeating-linear-gradient(
              -80deg,
              var(--color-border) 0px,
              var(--color-border) 8px,
              transparent 8px,
              transparent 16px
            )`,
          }}
        />
      </div>

      <div
        className="absolute inset-0 h-4 overflow-hidden rounded-xl"
        style={{
          width: animationReady ? `${currentProgress}%` : "0%",
          background: "var(--zeemee-gradient-2)",
          transition: "width 650ms ease-out",
        }}
      />

      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <img
            ref={imageRef}
            className="absolute top-1/2 h-8 w-8 -translate-y-1/2 cursor-pointer rounded-full border-2 border-background"
            src={user.profileUrl}
            alt={user.name}
            style={{
              left: animationReady
                ? `calc(${currentProgress}% - 16px)`
                : "calc(0% - 16px)",
              transition: "left 650ms ease-out",
            }}
            onMouseEnter={() => setPopoverOpen(true)}
            onMouseLeave={() => setPopoverOpen(false)}
          />
        </PopoverTrigger>
        <PopoverContent
          className="w-[200px] p-3"
          align="center"
          side="bottom"
          sideOffset={0}
          onMouseEnter={() => setPopoverOpen(true)}
          onMouseLeave={() => setPopoverOpen(false)}
        >
          <div className="flex flex-col gap-1.5">
            <div className="text-xs leading-4 font-medium text-primary">
              {t("strategyBoard.progress.single.popover.title")}
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs leading-3 text-muted-foreground">
                  {t("strategyBoard.progress.single.popover.completed")}
                </div>
                <div className="text-xs leading-3 font-medium text-green-600">
                  {user.completed}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs leading-3 text-muted-foreground">
                  {t("strategyBoard.progress.single.popover.skipped")}
                </div>
                <div className="text-xs leading-3 font-medium text-primary">
                  {user.skipped}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs leading-3 text-muted-foreground">
                  {t("strategyBoard.progress.single.popover.remaining")}
                </div>
                <div className="text-xs leading-3 font-medium text-primary">
                  {user.total - user.completed - user.skipped}
                </div>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
